import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import OptionsDropdown from 'modules/MediaDevicesSettings/OptionsDropdown';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';

interface ParentWorkspaceSelectorProps {
  selectedParentWorkspace: string | null;
  onParentWorkspaceChange: (workspaceId: string | null) => void;
}

const ParentWorkspaceSelector = ({
  selectedParentWorkspace,
  onParentWorkspaceChange,
}: ParentWorkspaceSelectorProps) => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { workspacesState } = useWorkspacesContext();

  const parentWorkspaces = workspacesState.rootWorkspaces.filter(({ isSubWorkspace }) => !isSubWorkspace);
  const permittedParentWorkspaces = parentWorkspaces.filter(
    ({ workspaceId }) => calculatePermissions(matrixClient, workspaceId).createSubWorkspace
  );

  useEffect(() => {
    const isParentWorkspaceMissing =
      selectedParentWorkspace &&
      !permittedParentWorkspaces.some(({ workspaceId }) => workspaceId === selectedParentWorkspace);
    if (isParentWorkspaceMissing) onParentWorkspaceChange(null);
  }, [selectedParentWorkspace, permittedParentWorkspaces, onParentWorkspaceChange]);

  useEffect(
    () => () => onParentWorkspaceChange(null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const parentWorkspaceOptions: DropdownSelectionProps[] = [
    {
      title: t('none'),
      isSelected: selectedParentWorkspace === null,
      onClick: () => onParentWorkspaceChange(null),
    },
    ...permittedParentWorkspaces.map(({ workspaceId }) => {
      const workspace = workspacesState.workspaces[workspaceId];
      return {
        title: workspace?.name,
        isSelected: selectedParentWorkspace === workspaceId,
        onClick: () => onParentWorkspaceChange(workspaceId),
      };
    }),
  ];

  return <OptionsDropdown options={parentWorkspaceOptions} selectClassName="h-12" />;
};

export default ParentWorkspaceSelector;
